import React from "react";
import { useSelector } from "react-redux";
import { HeaderBar } from "web/react/components/navigation/header-bar/header-bar";
import { NavigationMenu } from "web/react/components/navigation/navigation-menu";
import PreHeaderBar from "web/react/components/navigation/pre-header-bar/pre-header-bar";
import { SetPaidSessionIdOnLinks } from "web/react/components/set-paid-session-id-on-links/set-paid-session-id-on-links";
import { SkipNavigationButton } from "web/react/components/skip-navigation-button/skip-navigation-button";
import { MainNavigation as MainNavigationReduxInterface } from "web/redux/ducks/main-navigation";
import {
    AppHeaderBarSerializer,
    AppPreHeaderBarSerializer,
    HeaderGenderedMenuSerializer,
    MyListMenuSerializer,
} from "web/types/serializers";
import { DesktopMenu } from "./desktop-menu";
import { NavigationContextProvider } from "./navigation.context";
import * as styles from "./navigation.css";

function extractNavigation(state): MainNavigationReduxInterface {
    return state.mainNavigationReducer;
}

export interface NavigationProps {
    headerBar: AppHeaderBarSerializer;
    myListMenu: MyListMenuSerializer;
    preHeaderBar: AppPreHeaderBarSerializer;
    headerMenu: HeaderGenderedMenuSerializer;
    featuredDesignersUrl: string;
    blockBotsNavigation: boolean;
}

export function Navigation({
    headerBar,
    myListMenu,
    preHeaderBar,
    headerMenu,
    featuredDesignersUrl,
    blockBotsNavigation,
}: NavigationProps): React.ReactElement {
    const { selectedGender } = useSelector(extractNavigation);

    const genders = {
        women: "F",
        men: "M",
    };

    // Gender can be set from the request (userGender) or
    // from redux (selectedGender) by interacting with the navigation
    const currentGender = selectedGender || headerBar.selected_gender?.toLowerCase() || "women";

    return (
        <NavigationContextProvider>
            <SetPaidSessionIdOnLinks />
            <PreHeaderBar
                accountDropdown={preHeaderBar.account_dropdown}
                accountText={preHeaderBar.account_text}
                accountUrl={preHeaderBar.account_url}
                countryInfo={preHeaderBar.country_info}
                languageSelector={preHeaderBar.language_selector}
                registerUrl={preHeaderBar.register_url}
                showCountryPicker={preHeaderBar.show_country_picker}
            />

            <SkipNavigationButton />
            <HeaderBar
                {...headerBar}
                registerUrl={myListMenu.register_url}
                userDropdown={{
                    accountDropdown: preHeaderBar.account_dropdown,
                    accountText: preHeaderBar.account_text,
                    accountUrl: preHeaderBar.account_url,
                    registerUrl: preHeaderBar.register_url,
                }}
            />
            <DesktopMenu
                menu={headerMenu[genders[currentGender]]}
                featuredDesignersUrl={featuredDesignersUrl}
            />
            <NavigationMenu
                data={headerMenu}
                myListMenu={myListMenu}
                isAuthenticated={headerBar.is_authenticated}
                currentGender={currentGender}
                showCountryPicker={preHeaderBar.show_country_picker}
                currentCountry={preHeaderBar.country_info.country}
                currentCurrency={preHeaderBar.country_info.currency_indicator}
                allCountries={preHeaderBar.all_countries}
                languageSelector={preHeaderBar.language_selector}
                fullRender={blockBotsNavigation}
            />

            {/** empty div used for header-bar-search React portal @see header-bar.tsx*/}
            <div id="app-header-search-bar-mobile" className={styles.mobileSearchBar} />
        </NavigationContextProvider>
    );
}
